import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Onboarding from "@/pages/Onboarding";
import AuthCallback from "@/components/AuthCallback";
import Accounting from "@/pages/Accounting";
import Dashboard from "@/pages/Dashboard";
import Rooms from "@/pages/Rooms";
import CalendarPage from "@/pages/CalendarPage";
import Bookings from "@/pages/Bookings";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import PaymentResult from "@/pages/PaymentResult";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center bg-background" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!user) return;
    api.get("/settings")
      .then(({ data }) => setState(data.onboarding_completed ? "done" : "needed"))
      .catch(() => setState("done")); // never block the app if settings can't load
  }, [user]);
  if (state === "loading")
    return (
      <div className="flex h-screen items-center justify-center bg-background" data-testid="onboarding-check-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  if (state === "needed") return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Emergent OAuth returns with #session_id=... — detect synchronously during render (before
  // routes/Protected run) so the single-use code is exchanged first, with no login-page flash
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/payment/success" element={<PaymentResult type="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult type="cancel" />} />
      <Route path="/" element={<Protected><OnboardingGate><Layout /></OnboardingGate></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
